import React from 'react';

type AsyncState<T, A> = {
  loading: boolean;
  error: Error | null;
  value: (T & { lastArgs: A }) | null;
};

type AsyncAction<T, A> =
  | { type: 'start' }
  | { type: 'finish'; value: T & { lastArgs?: A } }
  | { type: 'error'; error: Error };

export const useAsync = <T, A = void>(fn: (args: A) => Promise<T>) => {
  const initialState: AsyncState<T, A> = {
    loading: false,
    error: null,
    value: null,
  };

  const stateReducer = (
    _: AsyncState<T, A>,
    action: AsyncAction<T, A>,
  ): AsyncState<T, A> => {
    switch (action.type) {
      case 'start':
        return { loading: true, error: null, value: _.value };
      case 'finish':
        return { loading: false, error: null, value: action.value as any };
      case 'error':
        return { loading: false, error: action.error, value: _.value };
      default:
        return _;
    }
  };

  const [state, dispatch] = React.useReducer(stateReducer, initialState);

  const run = async (args: A) => {
    try {
      dispatch({ type: 'start' });
      const value = await fn(args);
      if (!Array.isArray(value)) {
        dispatch({ type: 'finish', value: { ...value, lastArgs: args } });
      } else {
        dispatch({ type: 'finish', value });
      }
      return value;
    } catch (error) {
      dispatch({ type: 'error', error: error as Error });
      throw error as Error;
    }
  };

  return { ...state, run };
};
